import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://topnationapp.onrender.com';
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [transaction, setTransaction] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(sessionId, 0);
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setTransaction(data);
        setStatus('success');
        toast.success('Payment successful! You now own this country!');
        return;
      } else if (data.status === 'expired') {
        setStatus('error');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);

    } catch (error) {

      console.error('Error checking payment status:', error);

      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('error');
      }

    }
  };

  return (
    <div className="min-h-screen bg-[#050505] relative">
      <div className="noise-overlay" />

      <Navbar />

      <main className="relative z-10 max-w-2xl mx-auto px-6 py-16">

        {status === 'checking' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-panel p-12 text-center"
          >
            <Loader2 className="w-16 h-16 text-[#FFD700] mx-auto mb-4 animate-spin" />

            <h1 className="text-3xl font-['Cinzel'] text-white mb-2">
              Processing Payment
            </h1>

            <p className="text-gray-400">
              Please wait while we verify your payment...
            </p>

          </motion.div>
        )}

        {status === 'success' && transaction && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-panel-gold p-12 text-center"
          >

            <CheckCircle2 className="w-20 h-20 text-[#FFD700] mx-auto mb-6" />

            <h1 className="text-4xl font-['Cinzel'] text-[#FFD700] uppercase mb-4">
              Congratulations!
            </h1>

            <p className="text-xl text-white mb-8">
              You are now the proud owner of a nation!
            </p>

            <div className="bg-[#121212] rounded-lg p-6 mb-8 text-left">

              <div className="grid grid-cols-2 gap-4">

                <div>
                  <div className="text-sm text-gray-400">Country</div>
                  <div className="text-lg font-semibold text-white">
                    {transaction.country_code}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-400">Amount Paid</div>
                  <div className="text-lg font-semibold text-[#FFD700]">
                    €{transaction.amount.toFixed(2)}
                  </div>
                </div>

                {transaction.company_name && (
                  <div className="col-span-2">
                    <div className="text-sm text-gray-400">Company</div>
                    <div className="text-lg font-semibold text-white">
                      {transaction.company_name}
                    </div>
                  </div>
                )}

              </div>

            </div>

            <div className="flex gap-4 justify-center">

              <Button
                onClick={() => navigate('/dashboard')}
                className="btn-gold"
              >
                View Dashboard
              </Button>

              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="border-[#FFD700] text-[#FFD700] hover:bg-[#FFD700] hover:text-black"
              >
                Back to Map
              </Button>

            </div>

          </motion.div>
        )}

        {status === 'error' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-panel p-12 text-center"
          >

            <div className="text-red-500 text-6xl mb-4">❌</div>

            <h1 className="text-3xl font-['Cinzel'] text-white mb-2">
              Payment Failed
            </h1>

            <p className="text-gray-400 mb-8">
              There was an issue processing your payment.
            </p>

            <Button
              onClick={() => navigate('/')}
              className="btn-gold"
            >
              Try Again
            </Button>

          </motion.div>
        )}

        {status === 'timeout' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="glass-panel p-12 text-center"
          >

            <div className="text-yellow-500 text-6xl mb-4">⏱️</div>

            <h1 className="text-3xl font-['Cinzel'] text-white mb-2">
              Payment Pending
            </h1>

            <p className="text-gray-400 mb-8">
              Your payment is still being processed. Please check your email for confirmation.
            </p>

            <Button
              onClick={() => navigate('/dashboard')}
              className="btn-gold"
            >
              Go to Dashboard
            </Button>

          </motion.div>
        )}

      </main>
    </div>
  );
};

export default PaymentSuccess;
